import React, { useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "./firebase";

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSignin = async () => {
    if (!email) {
      setError("Please enter your email address");
      return;
    }
    if (!password) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    setError("");
  
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
      // Check if email is verified
      if (!userCredential.user.emailVerified) {
        // User exists but email not verified - redirect to verification page
        await auth.signOut();
        setError("Your email is not verified yet. Redirecting to verification page...");
        setTimeout(() => {
          window.location.href = `/verify?email=${encodeURIComponent(email)}`;
        }, 1500);
        return;
      }
    
      // Store user info
      localStorage.setItem("user", JSON.stringify({
        email: userCredential.user.email,
        uid: userCredential.user.uid
      }));
    
      window.location.href = "/";
    } catch (error) {
      setLoading(false);
      
      // Handle different error cases
      if (error.code === "auth/user-not-found") {
        setError("No account found with this email. Please sign up first.");
      } else if (error.code === "auth/invalid-credential" || error.code === "auth/wrong-password") {
        setError("Invalid email or password. Please try again.");
      } else if (error.code === "auth/too-many-requests") {
        setError("Too many failed attempts. Please try again later or reset your password.");
      } else if (error.code === "auth/invalid-email") {
        setError("Please enter a valid email address.");
      } else {
        setError(error.message);
      }
    }
  };

  const goToSignUp = () => {
    window.location.href = "/signup";
  };

  return (
    <div style={styles.container}>
      <style>{keyframes}</style>
      
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logoWrapper}>
          <img 
            src="logo.png" 
            alt="Brand Logo"
            style={styles.logo}
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = '<div style="width: 60px; height: 60px; background: linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%); border-radius: 50%; display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 25px rgba(139, 92, 246, 0.15);"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 8v8m-4-4h8"/></svg></div>';
            }}
          />
        </div>

        {/* Title */}
        <h1 style={styles.title}>Welcome Back</h1>
        <p style={styles.subtitle}>Sign in to continue your journey</p>

        {/* Error Message */}
        {error && (
          <div style={styles.errorContainer}>
            <svg style={styles.errorIcon} width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#EF4444" strokeWidth="2"/>
              <path d="M12 8v4M12 16h.01" stroke="#EF4444" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <span style={styles.errorText}>{error}</span>
          </div>
        )}

        {/* Email Input */}
        <div style={styles.inputWrapper}>
          <label style={styles.label}>Email Address</label>
          <input
            type="email"
            placeholder="your.email@example.com"
            style={{
              ...styles.input,
              ...(emailFocused ? styles.inputFocused : {})
            }}
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setError("");
            }}
            onFocus={() => setEmailFocused(true)}
            onBlur={() => setEmailFocused(false)}
            onKeyPress={(e) => e.key === 'Enter' && handleSignin()}
          />
        </div>

        {/* Password Input */}
        <div style={styles.inputWrapper}>
          <div style={styles.labelRow}>
            <label style={styles.label}>Password</label>
            <span 
              style={styles.forgotLink}
              onClick={() => window.location.href = "/forgot-password"}
              onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
            >
              Forgot password?
            </span>
          </div>
          <div style={styles.passwordContainer}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              style={{
                ...styles.input,
                ...styles.passwordInput,
                ...(passwordFocused ? styles.inputFocused : {})
              }}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setError("");
              }}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              onKeyPress={(e) => e.key === 'Enter' && handleSignin()}
            />
            <button
              type="button"
              style={styles.eyeButton}
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M3 3l18 18M10.5 10.5a2 2 0 002.45 2.45m5.05-5.05A10 10 0 0112 5C7 5 2.73 8.11 1 12.5a15.9 15.9 0 003.9 5.34M9.88 9.88a3 3 0 014.24 4.24M12 17c5 0 9.27-3.11 11-7.5a15.902 15.902 0 00-2.87-3.19" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="3" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Button */}
        <button 
          style={{
            ...styles.button,
            ...(loading ? styles.buttonDisabled : {})
          }}
          onClick={handleSignin}
          disabled={loading}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 40px rgba(139, 92, 246, 0.4)';
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 30px rgba(139, 92, 246, 0.3)';
            }
          }}
        >
          <span style={styles.buttonText}>
            {loading ? "Signing in..." : "Continue"}
          </span>
          {!loading && (
            <svg style={styles.buttonIcon} width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>

        {/* Divider */}
        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine}></div>
        </div>

        {/* Footer */}
        <p style={styles.footerTxt}>
          Don't have an account?{" "}
          <span 
            style={styles.link} 
            onClick={goToSignUp}
            onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
            onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
          >
            Sign up
          </span>
        </p>
      </div>
    </div>
  );
}

const keyframes = `
  @keyframes glow {
    0%, 100% {
      box-shadow: 0 0 40px rgba(139, 92, 246, 0.15),
                  0 0 80px rgba(139, 92, 246, 0.1),
                  0 20px 60px rgba(0, 0, 0, 0.08);
    }
    50% {
      box-shadow: 0 0 60px rgba(139, 92, 246, 0.25),
                  0 0 120px rgba(139, 92, 246, 0.15),
                  0 25px 70px rgba(0, 0, 0, 0.1);
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-10px); }
  }

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 480px) {
    body {
      font-size: 14px;
    }
  }
`;

const styles = {
  container: {
    minHeight: "100vh",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
    padding: "20px",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
  },

  card: {
    width: "100%",
    maxWidth: "440px",
    padding: "48px 40px",
    background: "#ffffff",
    borderRadius: "24px",
    border: "1px solid rgba(139, 92, 246, 0.08)",
    textAlign: "center",
    animation: "glow 4s ease-in-out infinite, fadeIn 0.6s ease-out",
    position: "relative",
    boxSizing: "border-box",
  },

  logoWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "32px",
    animation: "float 3s ease-in-out infinite",
  },

  logo: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    objectFit: "contain",
    boxShadow: "0 8px 25px rgba(139, 92, 246, 0.15)",
  },

  title: {
    fontSize: "32px",
    fontWeight: "700",
    color: "#1a1a2e",
    margin: "0 0 8px 0",
    letterSpacing: "-0.5px",
    lineHeight: "1.2",
  },

  subtitle: {
    margin: "0 0 32px 0",
    color: "#6B7280",
    fontSize: "15px",
    fontWeight: "400",
    lineHeight: "1.5",
  },

  errorContainer: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "12px 16px",
    background: "#FEE2E2",
    border: "1px solid #FCA5A5",
    borderRadius: "10px",
    marginBottom: "24px",
    animation: "slideDown 0.3s ease-out",
  },

  errorIcon: {
    flexShrink: 0,
  },

  errorText: {
    fontSize: "14px",
    color: "#DC2626",
    textAlign: "left",
    lineHeight: "1.4",
  },

  inputWrapper: {
    marginBottom: "20px",
    textAlign: "left",
  },

  label: {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "8px",
  },

  labelRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "8px",
  },

  forgotLink: {
    fontSize: "13px",
    color: "#8B5CF6",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    textDecoration: "none",
  },

  input: {
    width: "100%",
    padding: "16px 20px",
    fontSize: "15px",
    borderRadius: "12px",
    border: "2px solid #e5e7eb",
    outline: "none",
    background: "#fafafa",
    transition: "all 0.3s ease",
    fontFamily: "inherit",
    color: "#1a1a2e",
    boxSizing: "border-box",
  },

  inputFocused: {
    border: "2px solid #8B5CF6",
    background: "#ffffff",
    boxShadow: "0 0 0 4px rgba(139, 92, 246, 0.1)",
  },

  passwordContainer: {
    position: "relative",
  },

  passwordInput: {
    paddingRight: "50px",
  },

  eyeButton: {
    position: "absolute",
    right: "16px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "4px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "opacity 0.2s ease",
    opacity: 0.6,
  },

  button: {
    marginTop: "8px",
    width: "100%",
    padding: "16px 24px",
    borderRadius: "12px",
    background: "linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)",
    color: "#ffffff",
    fontWeight: "600",
    fontSize: "16px",
    border: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    boxShadow: "0 8px 30px rgba(139, 92, 246, 0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    fontFamily: "inherit",
    position: "relative",
    overflow: "hidden",
  },

  buttonDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },

  buttonText: {
    flex: 1,
    textAlign: "center",
  },

  buttonIcon: {
    transition: "transform 0.3s ease",
    position: "absolute",
    right: "24px",
  },

  divider: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    margin: "32px 0",
  },

  dividerLine: {
    flex: 1,
    height: "1px",
    background: "linear-gradient(90deg, transparent, #e5e7eb, transparent)",
  },

  dividerText: {
    fontSize: "13px",
    color: "#9CA3AF",
    fontWeight: "500",
    textTransform: "lowercase",
  },

  footerTxt: {
    margin: "0",
    fontSize: "14px",
    color: "#6B7280",
    fontWeight: "400",
    lineHeight: "1.5",
  },

  link: {
    color: "#8B5CF6",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    textDecoration: "none",
  },
};